import { gql, Reference } from '@apollo/client';
import { useSentry } from '@starrez/starryutils';
import React, { FC, useEffect, useState } from 'react';
import { IMainDirectoryListItem, IMainDirectoryListVariables, useMainDirectoryListQuery } from '../../../graphql/useAllMainDirectoryListQuery';
import { useFavouriteMainDirectoryListItem } from '../../../graphql/useMutateAllMainDirectoryList';

declare let SEND_TO_SENTRY: boolean;

interface IViewPicker {
    isPickerOpen: boolean;
    favouriteViews?: IMainDirectoryListItem[];
    activeView: IMainDirectoryListItem | undefined;
    openPicker: () => void;
    closePicker: () => void;
    updateActiveView: (viewItem: IMainDirectoryListItem) => void;
    updateFavouriteViews: (viewItem: IMainDirectoryListItem) => void;
    loadingState: boolean;
}

const ViewPickerContext = React.createContext<IViewPicker>({} as IViewPicker);
export const useViewPicker = () => React.useContext(ViewPickerContext);

interface IViewPickerProvider {
    onChange: (viewItem: IMainDirectoryListItem) => void;
    currentView: IMainDirectoryListItem | undefined;
}

export const ViewPickerProvider: FC<IViewPickerProvider> = ({ onChange, children, currentView }) => {
    const [isPickerOpen, setPickerState] = useState<boolean>(false);
    const [updateFavouriteMainDirectoryListItem] = useFavouriteMainDirectoryListItem();
    const { captureException } = useSentry();

    const variables: IMainDirectoryListVariables = {
        filter: [{ favourite: { eq: true } }],
        skip: 0,
        limit: 10,
        sort: [{
            field: 'DESCRIPTION',
            order: 'ASC'
        }],
    };

    const { data, loading } = useMainDirectoryListQuery(variables);
    const favouriteViews: IMainDirectoryListItem[] | undefined = data?.allMainDirectoryList?.nodes;
    const [activeView, setActiveView] = useState(favouriteViews ? favouriteViews[0] : currentView);

    useEffect(() => {
        if (currentView) {
            setActiveView(currentView);
        }
    }, [currentView]);

    const handleUpdateFavouriteViews = (viewItem: IMainDirectoryListItem) => {
        if (!viewItem.id) {
            return;
        }
        const listID = +viewItem.id;
        const favourite = !viewItem.favourite;
        const listType = viewItem.listType;

        updateFavouriteMainDirectoryListItem({
            variables: { params: { listID, favourite, listType } },
            optimisticResponse: {
                updateMainDirectoryList: {
                    hasErrors: false,
                    result: {
                        ...viewItem,
                        favourite,
                        __typename: 'DirectoryList',
                    },
                    __typename: 'DirectoryListMutateResponse',
                },
            },
            update: (cache) => {
                try {
                    cache.modify({
                        fields: {
                            allMainDirectoryList: (existingRefs, { readField }) => {
                                const newDirectoryListRef = cache.writeFragment({
                                    data: { ...viewItem, favourite },
                                    fragment: gql`
                                        fragment NewDirectoryList on DirectoryList {
                                            id
                                            dateCreated {
                                                js
                                            }
                                            dateModified {
                                                js
                                            }
                                            description
                                            caption
                                            favourite
                                            folder
                                            listType
                                            parentID
                                            recordType
                                            securityUserID
                                            tableName
                                        }
                                    `,
                                });

                                if (favourite) {
                                    // Quick safety check - if the new DirectoryList is already
                                    // present in the cache, we don't need to add it again.
                                    if (existingRefs.nodes.some((ref: Reference) => readField('id', ref) === viewItem.id)) {
                                        return existingRefs;
                                    }

                                    return {
                                        ...existingRefs,
                                        nodes: [...existingRefs.nodes, newDirectoryListRef],
                                    };
                                } else {
                                    return {
                                        ...existingRefs,
                                        nodes: existingRefs.nodes.map((ref: Reference) => {
                                            if (readField('id', ref) === viewItem.id) {
                                                return { ...ref, newDirectoryListRef };
                                            }
                                            return ref;
                                        }),
                                    };
                                }
                            },
                        },
                    });
                } catch (error) {
                    if (SEND_TO_SENTRY && error) {
                        captureException(error);
                    }
                }
            },
        });
    };

    const handleUpdateActiveView = (viewItem: IMainDirectoryListItem) => {
        setActiveView(viewItem);
        setPickerState(false);
        onChange(viewItem);
    };

    return (
        <ViewPickerContext.Provider
            value={{
                isPickerOpen: isPickerOpen,
                activeView: activeView,
                favouriteViews: favouriteViews,
                openPicker: () => setPickerState(true),
                closePicker: () => setPickerState(false),
                updateActiveView: handleUpdateActiveView,
                updateFavouriteViews: handleUpdateFavouriteViews,
                loadingState: loading,
            }}
        >
            {children}
        </ViewPickerContext.Provider>
    );
};
